"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import type { SurfSpot } from "@/lib/types/surf-spot"
import { calculateDistance } from "@/lib/utils/geolocation"
import { createSurfSpotSlug } from "@/lib/utils/slug"

// Extended SurfSpot type with optional distance property
interface SurfSpotWithDistance extends SurfSpot {
  distance?: number
}

interface UseInfiniteSurfSpotsReturn {
  spots: SurfSpotWithDistance[]
  loading: boolean
  loadingMore: boolean
  error: string | null
  hasMore: boolean
  total: number | null // Total count of all spots
  loadMore: () => void
  refetch: () => void
}

interface UseInfiniteSurfSpotsOptions {
  userLatitude?: number | null
  userLongitude?: number | null
  maxDistance?: number | null
  sortByDistance?: boolean
  country?: string | null
  pageSize?: number // Number of spots per page
}

interface SurfSpotsApiResponse {
  success: boolean
  data?: {
    spots: SurfSpot[]
    count: number
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
  error?: string
}

/**
 * Hook for infinite scroll surf spots fetching with pagination
 * 
 * Automatically loads more spots as user scrolls
 * Supports distance filtering and sorting
 */
export function useInfiniteSurfSpots(
  options: UseInfiniteSurfSpotsOptions = {}
): UseInfiniteSurfSpotsReturn {
  const { userLatitude, userLongitude, maxDistance, sortByDistance, country, pageSize = 20 } = options

  const [spots, setSpots] = useState<SurfSpotWithDistance[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [total, setTotal] = useState<number | null>(null) // Track total count
  const [offset, setOffset] = useState(0)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Fetch spots with pagination
  const fetchSpots = useCallback(
    async (currentOffset: number, isInitialLoad: boolean = false) => {
      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const controller = new AbortController()
      abortControllerRef.current = controller

      if (isInitialLoad) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }
      setError(null)

      try {
        // Build query parameters
        const params = new URLSearchParams({
          limit: pageSize.toString(),
          offset: currentOffset.toString(),
        })

        if (country) {
          params.set('country', country)
        }

        const response = await fetch(`/api/surf-spots?${params.toString()}`, {
          signal: controller.signal,
          credentials: "include",
        })

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`)
        }

        const data: SurfSpotsApiResponse = await response.json()

        if (!data.success || !data.data) {
          throw new Error(data.error || "Failed to fetch surf spots")
        }

        // Process spots: add slugs, calculate distances, filter, sort
        let processedSpots: SurfSpotWithDistance[] = data.data.spots.map((spot) => ({
          ...spot,
          slug: spot.slug || createSurfSpotSlug(spot.name, spot.location, spot.country),
        }))

        // Calculate distance if user location is provided
        if (
          userLatitude !== null &&
          userLatitude !== undefined &&
          userLongitude !== null &&
          userLongitude !== undefined
        ) {
          processedSpots = processedSpots.map((spot) => ({
            ...spot,
            distance: calculateDistance(
              userLatitude,
              userLongitude,
              Number(spot.latitude),
              Number(spot.longitude)
            ),
          }))

          // Filter by max distance if specified
          if (maxDistance !== null && maxDistance !== undefined) {
            processedSpots = processedSpots.filter(
              (spot) => spot.distance !== undefined && spot.distance <= maxDistance
            )
          }

          // Sort by distance if requested
          if (sortByDistance) {
            processedSpots.sort((a, b) => (a.distance || 0) - (b.distance || 0))
          }
        }

        // Update state
        // Use the actual API response count, not filtered count, for offset calculation
        const actualCount = data.data.spots.length // Count from API before filtering
        
        if (isInitialLoad) {
          setSpots(processedSpots)
        } else {
          setSpots((prev) => [...prev, ...processedSpots])
        }

        setHasMore(data.data.hasMore)
        // Update total count from API (only set on initial load or if it changes)
        if (isInitialLoad || total === null) {
          setTotal(data.data.total || data.data.count || null)
        }
        // Update offset based on actual API response, not filtered results
        // This ensures pagination works correctly even if client-side filtering removes spots
        setOffset(currentOffset + actualCount)
        
        if (process.env.NODE_ENV === "development") {
          console.log("[Infinite Scroll] Loaded spots:", {
            currentOffset,
            actualCount,
            processedCount: processedSpots.length,
            newOffset: currentOffset + actualCount,
            hasMore: data.data.hasMore,
            total: data.data.total || data.data.count
          })
        }
      } catch (err: any) {
        if (err.name === "AbortError") {
          // Request was cancelled, ignore
          return
        }
        console.error("Error fetching surf spots:", err)
        setError(err.message || "Failed to load surf spots")
      } finally {
        setLoading(false)
        setLoadingMore(false)
        abortControllerRef.current = null
      }
    },
    [userLatitude, userLongitude, maxDistance, sortByDistance, country, pageSize]
  )

  // Initial load
  useEffect(() => {
    setSpots([])
    setOffset(0)
    setHasMore(true)
    fetchSpots(0, true)
  }, [fetchSpots])

  // Load more spots
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      fetchSpots(offset, false)
    }
  }, [loadingMore, hasMore, loading, offset, fetchSpots])

  // Refetch all spots
  const refetch = useCallback(() => {
    setSpots([])
    setOffset(0)
    setHasMore(true)
    fetchSpots(0, true)
  }, [fetchSpots])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    spots,
    loading,
    loadingMore,
    error,
    hasMore,
    total,
    loadMore,
    refetch,
  }
}

